import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import Select from "react-select";
import Alert from "../../ui/Alert.jsx";
import { customSelectStylesLarge } from "../../../config/selectStyles";
import { Save, RefreshCw, Package } from "lucide-react";
import FroalaEditor from "react-froala-wysiwyg";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/js/languages/de.js";
import froalaConfig from "../../../config/froalaConfig";

const CreateArticleModal = ({ isOpen, onClose, onCreated }) => {
    const [formData, setFormData] = useState({
        categoryId: '',
        subcategoryId: '',
        categoryNumber: '',
        subcategoryNumber: '',
        articleNumber: '001',
        title: '',
        description: '',
        count: 1,
        unit: '',
        taxType: 'netto',
        tax: '',
        price: ''
    });

    const [dropdownData, setDropdownData] = useState({
        categories: [],
        subcategories: [],
        units: [],
        taxes: []
    });

    const [userSettings, setUserSettings] = useState({
        currency: 'EUR',
        currencySymbol: '€',
        isTaxLiable: true
    });

    const [articlePreview, setArticlePreview] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ message: "", type: "" });

    useEffect(() => {
        if (isOpen) {
            fetchDropdownData();
            resetForm();
        }
    }, [isOpen]);

    const resetForm = () => {
        setFormData({
            categoryId: '',
            subcategoryId: '',
            categoryNumber: '',
            subcategoryNumber: '',
            articleNumber: '001',
            title: '',
            description: '',
            count: 1,
            unit: '',
            taxType: 'netto',
            tax: '',
            price: ''
        });
        setArticlePreview([]);
        setAlert({ message: "", type: "" });
    };

    const fetchDropdownData = async () => {
        try {
            const [categoriesRes, unitsRes, taxesRes, settingsRes] = await Promise.all([
                fetch("index.php/articles/getCategoriesDropdown"),
                fetch("index.php/documents/getUnits"),
                fetch("index.php/documents/getTaxes"),
                fetch("index.php/articles/getUserSettings")
            ]);

            const [categories, units, taxes, settings] = await Promise.all([
                categoriesRes.json(),
                unitsRes.json(),
                taxesRes.json(),
                settingsRes.json()
            ]);

            setDropdownData({
                categories: categories,
                subcategories: [],
                units: units,
                taxes: taxes
            });

            setUserSettings(settings);

            // Set default tax if available
            if (taxes.length > 0 && settings.isTaxLiable) {
                setFormData(prev => ({
                    ...prev,
                    tax: taxes[0].value
                }));
            }

            // Set default unit if available
            if (units.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    unit: units[0].value
                }));
            }

        } catch (error) {
            console.error("Fehler beim Laden der Dropdown-Daten:", error);
            setAlert({ message: "Fehler beim Laden der Daten", type: "danger" });
        }
    };

    const handleCategoryChange = async (selected) => {
        const categoryId = selected ? selected.value : '';
        const categoryNumber = selected ? selected.number : '';

        setFormData(prev => ({
            ...prev,
            categoryId: categoryId,
            categoryNumber: categoryNumber,
            subcategoryId: '',
            subcategoryNumber: '',
            articleNumber: '001'
        }));

        if (categoryId) {
            // Fetch subcategories for this category
            try {
                const response = await fetch(`index.php/articles/getSubCategoriesForCategory?categoryId=${categoryId}`);
                const subcategories = await response.json();
                setDropdownData(prev => ({
                    ...prev,
                    subcategories: subcategories
                }));
            } catch (error) {
                console.error("Fehler beim Laden der Unterkategorien:", error);
            }

            // Fetch article preview
            fetchArticlePreview(categoryId, '');
        } else {
            setDropdownData(prev => ({
                ...prev,
                subcategories: []
            }));
            setArticlePreview([]);
        }
    };

    const handleSubcategoryChange = async (selected) => {
        const subcategoryId = selected ? selected.value : '';
        const subcategoryNumber = selected ? selected.number : '';

        setFormData(prev => ({
            ...prev,
            subcategoryId: subcategoryId,
            subcategoryNumber: subcategoryNumber
        }));

        if (formData.categoryId && subcategoryId) {
            // Fetch next article number
            try {
                const response = await fetch(`index.php/articles/getNextArticleNumber?categoryId=${formData.categoryId}&subcategoryId=${subcategoryId}`);
                const data = await response.json();
                setFormData(prev => ({
                    ...prev,
                    articleNumber: data.nextNumber || '001'
                }));
            } catch (error) {
                console.error("Fehler beim Laden der Artikelnummer:", error);
            }

            // Fetch article preview
            fetchArticlePreview(formData.categoryId, subcategoryId);
        } else if (formData.categoryId) {
            fetchArticlePreview(formData.categoryId, '');
        }
    };

    const fetchArticlePreview = async (categoryId, subcategoryId) => {
        try {
            let url = `index.php/articles/getArticlePreviewJson?categoryId=${categoryId}`;
            if (subcategoryId) {
                url += `&subcategoryId=${subcategoryId}`;
            }
            const response = await fetch(url);
            const data = await response.json();
            setArticlePreview(data);
        } catch (error) {
            console.error("Fehler beim Laden der Artikel-Vorschau:", error);
        }
    };

    const handleDescriptionChange = (content) => {
        setFormData(prev => ({ ...prev, description: content }));
    };

    const formatCurrency = (value) => {
        let cleaned = value.replace(/[^\d,.-]/g, '');
        return cleaned;
    };

    const handlePriceChange = (e) => {
        const formatted = formatCurrency(e.target.value);
        setFormData(prev => ({ ...prev, price: formatted }));
    };

    const handleSubmit = async (e, saveAndNew = false) => {
        e.preventDefault();
        setAlert({ message: "", type: "" });

        // Validation
        const errors = [];
        if (!formData.title.trim()) errors.push("Titel ist erforderlich");
        if (!formData.price.trim()) errors.push("Preis ist erforderlich");
        if (userSettings.isTaxLiable && !formData.tax) errors.push("MwSt. ist erforderlich");

        if (errors.length > 0) {
            setAlert({ message: errors.join(", "), type: "danger" });
            return;
        }

        setLoading(true);

        try {
            const postData = {
                title: formData.title,
                description: formData.description,
                categoryId: formData.categoryId || 0,
                subcategoryId: formData.subcategoryId || 0,
                articleNumber: formData.articleNumber,
                taxType: userSettings.isTaxLiable ? formData.taxType : 'netto',
                tax: userSettings.isTaxLiable ? formData.tax : 1,
                price: formData.price,
                unit: formData.unit || 1,
                count: formData.count || 1
            };

            const response = await fetch('index.php/articles/createArticle', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(postData)
            });

            const result = await response.json();

            if (result.success) {
                setAlert({ message: result.message, type: "success" });

                if (saveAndNew) {
                    setTimeout(() => {
                        resetForm();
                        fetchDropdownData();
                        setLoading(false);
                        onCreated();
                    }, 500);
                } else {
                    setTimeout(() => {
                        onCreated();
                        onClose();
                        setLoading(false);
                    }, 1000);
                }
            } else {
                setAlert({ message: result.error || "Fehler beim Erstellen", type: "danger" });
                setLoading(false);
            }

        } catch (error) {
            console.error("Fehler beim Erstellen:", error);
            setAlert({ message: "Ein Fehler ist aufgetreten", type: "danger" });
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Neuer Artikel"
            className="articles-modal-content"
            overlayClassName="articles-modal-overlay"
        >
            <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                {/* Header */}
                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 className="modal-title">
                        <Package size={24} />
                        Neuer Artikel
                    </h4>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={onClose}
                        style={{ opacity: 0.5 }}
                    ></button>
                </div>

                {/* Body */}
                <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                    {alert.message && (
                        <Alert
                            type={alert.type}
                            message={alert.message}
                            onClose={() => setAlert({ message: "", type: "" })}
                        />
                    )}

                    <div className="row">
                        {/* Left Column - Form */}
                        <div className="col-md-7">
                            <form id="createArticleForm" onSubmit={(e) => handleSubmit(e, false)}>
                                {/* Category Selection */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Kategorie
                                        </label>
                                        <Select
                                            options={dropdownData.categories}
                                            styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Kategorie auswählen..."
                                            isClearable
                                            value={dropdownData.categories.find(c => c.value === formData.categoryId) || null}
                                            onChange={handleCategoryChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Unterkategorie
                                        </label>
                                        <Select
                                            options={dropdownData.subcategories}
                                            styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Unterkategorie auswählen..."
                                            isClearable
                                            isDisabled={!formData.categoryId}
                                            value={dropdownData.subcategories.find(s => s.value === formData.subcategoryId) || null}
                                            onChange={handleSubcategoryChange}
                                        />
                                    </div>
                                </div>

                                {/* Number Fields */}
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Kategorie-Nr.
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control bg-light"
                                            value={formData.categoryNumber}
                                            readOnly
                                            style={{
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                padding: '0.625rem 0.875rem'
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Unterkategorie-Nr.
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control bg-light"
                                            value={formData.subcategoryNumber}
                                            readOnly
                                            style={{
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                padding: '0.625rem 0.875rem'
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Artikelnummer
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.articleNumber}
                                            onChange={(e) => setFormData(prev => ({ ...prev, articleNumber: e.target.value }))}
                                            style={{
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                padding: '0.625rem 0.875rem'
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="mb-3">
                                    <label className="form-label" style={{
                                        fontWeight: '500',
                                        color: 'var(--text-primary, #2c3e50)',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Titel *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.title}
                                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="Artikelbezeichnung"
                                        style={{
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-medium, #cbd5e1)',
                                            padding: '0.625rem 0.875rem'
                                        }}
                                    />
                                </div>

                                {/* Description */}
                                <div className="mb-3">
                                    <label className="form-label" style={{
                                        fontWeight: '500',
                                        color: 'var(--text-primary, #2c3e50)',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Beschreibung
                                    </label>
                                    <div style={{
                                        border: '1px solid var(--border-medium, #cbd5e1)',
                                        borderRadius: '8px',
                                        overflow: 'hidden'
                                    }}>
                                        <FroalaEditor
                                            tag="textarea"
                                            config={{
                                                ...froalaConfig,
                                                height: 150,
                                                placeholderText: 'Beschreibung eingeben...'
                                            }}
                                            model={formData.description}
                                            onModelChange={handleDescriptionChange}
                                        />
                                    </div>
                                </div>

                                {/* Count and Unit */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Anzahl
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={formData.count}
                                            onChange={(e) => setFormData(prev => ({ ...prev, count: parseInt(e.target.value) || 1 }))}
                                            min="1"
                                            style={{
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                padding: '0.625rem 0.875rem'
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Einheit
                                        </label>
                                        <Select
                                            options={dropdownData.units}
                                            styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Einheit auswählen..."
                                            value={dropdownData.units.find(u => u.value === formData.unit) || null}
                                            onChange={(selected) => setFormData(prev => ({
                                                ...prev,
                                                unit: selected ? selected.value : ''
                                            }))}
                                        />
                                    </div>
                                </div>

                                {/* Tax Type, Tax Rate, Price */}
                                <div className="row mb-3">
                                    {userSettings.isTaxLiable && (
                                        <>
                                            <div className="col-md-4">
                                                <label className="form-label" style={{
                                                    fontWeight: '500',
                                                    color: 'var(--text-primary, #2c3e50)',
                                                    marginBottom: '0.5rem'
                                                }}>
                                                    Steuerart
                                                </label>
                                                <Select
                                                    options={[
                                                        { value: 'netto', label: 'Netto' },
                                                        { value: 'brutto', label: 'Brutto' }
                                                    ]}
                                                    styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                                    value={{ value: formData.taxType, label: formData.taxType === 'netto' ? 'Netto' : 'Brutto' }}
                                                    onChange={(selected) => setFormData(prev => ({
                                                        ...prev,
                                                        taxType: selected ? selected.value : 'netto'
                                                    }))}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label" style={{
                                                    fontWeight: '500',
                                                    color: 'var(--text-primary, #2c3e50)',
                                                    marginBottom: '0.5rem'
                                                }}>
                                                    MwSt. *
                                                </label>
                                                <Select
                                                    options={dropdownData.taxes}
                                                    styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                                    placeholder="MwSt. auswählen..."
                                                    value={dropdownData.taxes.find(t => t.value === formData.tax) || null}
                                                    onChange={(selected) => setFormData(prev => ({
                                                        ...prev,
                                                        tax: selected ? selected.value : ''
                                                    }))}
                                                />
                                            </div>
                                        </>
                                    )}
                                    <div className={userSettings.isTaxLiable ? "col-md-4" : "col-12"}>
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Preis ({formData.taxType === 'brutto' ? 'Brutto' : 'Netto'}) *
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.price}
                                                onChange={handlePriceChange}
                                                placeholder="0,00"
                                                style={{
                                                    borderRadius: '8px 0 0 8px',
                                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                                    padding: '0.625rem 0.875rem'
                                                }}
                                            />
                                            <span className="input-group-text" style={{
                                                borderRadius: '0 8px 8px 0',
                                                background: 'var(--bg-secondary, #f5f7f9)',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                borderLeft: 'none'
                                            }}>{userSettings.currency}</span>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Right Column - Article Preview */}
                        <div className="col-md-5">
                            <div style={{
                                background: 'var(--bg-secondary, #f5f7f9)',
                                borderRadius: '12px',
                                padding: '1rem',
                                height: '100%',
                                minHeight: '400px'
                            }}>
                                <h6 style={{
                                    fontWeight: '600',
                                    color: 'var(--text-primary, #2c3e50)',
                                    marginBottom: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <Package size={18} />
                                    Vorhandene Artikel
                                </h6>

                                {articlePreview.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center',
                                        color: 'var(--text-tertiary, #94a3b8)',
                                        padding: '2rem 1rem'
                                    }}>
                                        <Package size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
                                        <p style={{ margin: 0, fontSize: '0.875rem' }}>
                                            {formData.categoryId
                                                ? 'Keine Artikel in dieser Kategorie'
                                                : 'Kategorie auswählen, um Artikel anzuzeigen'}
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                        <table className="table table-sm" style={{ fontSize: '0.8125rem' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ fontWeight: '600', color: 'var(--text-secondary, #64748b)' }}>Titel</th>
                                                    <th style={{ fontWeight: '600', color: 'var(--text-secondary, #64748b)', textAlign: 'right' }}>Preis</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {articlePreview.map((article) => (
                                                    <tr key={article.id}>
                                                        <td style={{ color: 'var(--text-primary, #2c3e50)' }}>
                                                            {article.title}
                                                        </td>
                                                        <td style={{ color: 'var(--text-primary, #2c3e50)', textAlign: 'right' }}>
                                                            {article.amountFormatted} {userSettings.currency}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <button
                        type="button"
                        onClick={(e) => handleSubmit(e, true)}
                        disabled={loading}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            border: '1px solid var(--border-medium, #cbd5e1)',
                            borderRadius: '8px',
                            background: 'var(--bg-card, #ffffff)',
                            color: 'var(--text-primary, #2c3e50)',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            cursor: loading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <RefreshCw size={16} />
                        Speichern & Neu
                    </button>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={onClose}
                            disabled={loading}
                            style={{
                                borderRadius: '8px',
                                padding: '0.5rem 1rem'
                            }}
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            form="createArticleForm"
                            className="btn btn-primary"
                            disabled={loading}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                borderRadius: '8px',
                                padding: '0.5rem 1rem',
                                background: 'var(--accent-slate, #5b7c8d)',
                                borderColor: 'var(--accent-slate, #5b7c8d)'
                            }}
                        >
                            <Save size={16} />
                            {loading ? "Wird erstellt..." : "Speichern"}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default CreateArticleModal;
